import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Users, X, Search, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { useActivityBar } from '../contexts/ActivityBarContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ORANGE = '#ee7203';

/**
 * Customer Group — opened from Customer Management > Pricing > Customer Group.
 * Search the CRM and tick any other accounts this customer also trades under (e.g. a separate account per branch or
 * site); once grouped, every member account's quotes and orders feed into ONE combined price list, and the Pricing
 * tab shows the same list no matter which member account you open it from.
 *
 * Props: isOpen, onClose, account { id, name } (the account already open — always included, can't be un-ticked),
 * existingGroup (the group this account is already in, if any — shown read-only with an Ungroup option),
 * onGrouped() called after a group is created or removed, to reload the Pricing tab.
 */
export default function CustomerGroupModal({ isOpen, onClose, account, existingGroup, onGrouped }) {
  const { modalUiV2 } = useUIPreview();
  const activityBar = useActivityBar();
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(() => new Map()); // account_id -> name
  const [saving, setSaving] = useState(false);
  const [ungrouping, setUngrouping] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setResults([]);
    const m = new Map();
    if (account?.id) m.set(account.id, account.name || '');
    setPicked(m);
  }, [isOpen, account?.id, account?.name]);

  useEffect(() => {
    if (!isOpen || existingGroup) return undefined;
    if (query.trim().length < 2) { setResults([]); return undefined; }
    setSearching(true);
    const t = setTimeout(() => {
      api.get(`${API}/zoho/search-accounts`, { params: { q: query.trim() } })
        .then((r) => setResults(r.data?.accounts || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [query, isOpen, existingGroup]);

  if (!isOpen) return null;

  const toggle = (a) => {
    if (a.id === account?.id) return; // the account already open can't be removed
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(a.id)) next.delete(a.id); else next.set(a.id, a.name);
      return next;
    });
  };

  const create = async () => {
    if (picked.size < 2) return toast.error('Pick at least one other account to group with');
    setSaving(true);
    try {
      await api.post(`${API}/customer-pricing/account-groups`, {
        accounts: Array.from(picked, ([account_id, name]) => ({ account_id, name })),
      });
      toast.success('Customer Group created — their price lists are now combined');
      onGrouped?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create the group');
    } finally {
      setSaving(false);
    }
  };

  const ungroup = async () => {
    if (!existingGroup?.id) return;
    setUngrouping(true);
    try {
      await api.delete(`${API}/customer-pricing/account-group/${existingGroup.id}`);
      toast.success('Ungrouped — each account now has its own price list again');
      onGrouped?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to ungroup');
    } finally {
      setUngrouping(false);
    }
  };

  return createPortal(
    <div
      className="fixed left-0 right-0 bottom-0 flex items-center justify-center p-4 bg-black/50"
      style={{ top: modalUiV2 ? activityBar.headerHeight : 0, zIndex: 130 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="customer-group-overlay"
    >
      <div
        className="w-full max-w-[620px] max-h-full flex flex-col rounded-3xl bg-white shadow-2xl border-2 border-orange-300 overflow-hidden"
        data-testid="customer-group-modal"
      >
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-orange-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}
            >
              <Users className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-extrabold text-gray-900 leading-tight">Customer Group</h3>
              <p className="text-[11px] text-gray-500 truncate">{account?.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 rounded-full p-1.5 hover:bg-gray-100 flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        {existingGroup ? (
          <div className="p-5 space-y-3">
            <p className="text-sm text-gray-700">
              This customer's price list already combines <strong>{existingGroup.members?.length || 0}</strong> accounts:
            </p>
            <div className="space-y-1.5">
              {(existingGroup.members || []).map((m) => (
                <div
                  key={m.account_id}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${m.account_id === account?.id ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-gray-50'}`}
                  data-testid={`customer-group-member-${m.account_id}`}
                >
                  <span className="font-semibold text-gray-900 truncate">{m.name}</span>
                  {m.account_id === account?.id && <span className="text-[10px] text-orange-600 font-bold ml-auto flex-shrink-0">CURRENT</span>}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose} className="rounded-xl h-10 px-4 text-sm">Close</Button>
              <Button
                onClick={ungroup}
                disabled={ungrouping}
                variant="outline"
                className="rounded-xl h-10 px-4 text-sm font-semibold border-2 border-red-300 text-red-600 hover:bg-red-50"
                data-testid="customer-group-ungroup-btn"
              >
                {ungrouping ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Ungrouping…</> : 'Ungroup'}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="px-5 pt-4 flex-shrink-0">
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">
                Search for the customer's other CRM accounts
              </Label>
              <div className="relative mt-1.5">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300 pointer-events-none" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Type a company / account name…"
                  className="pl-9 rounded-xl border-2 border-gray-300 h-10"
                  autoFocus
                  data-testid="customer-group-search"
                />
              </div>
            </div>

            <div className="px-5 py-3 flex-1 min-h-0 overflow-y-auto space-y-3">
              {picked.size > 0 && (
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Will be grouped together</p>
                  <div className="flex flex-wrap gap-1.5" data-testid="customer-group-selected">
                    {Array.from(picked, ([id, name]) => (
                      <span
                        key={id}
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${id === account?.id ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-700'}`}
                      >
                        {name}
                        {id !== account?.id && (
                          <button onClick={() => toggle({ id, name })} className="text-gray-400 hover:text-gray-700">
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {searching ? (
                <div className="flex items-center justify-center py-8 gap-2 text-gray-400 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> Searching…
                </div>
              ) : results.length > 0 ? (
                <div className="border border-gray-200 rounded-xl divide-y overflow-hidden">
                  {results.map((a) => {
                    const isCurrent = a.id === account?.id;
                    const isPicked = picked.has(a.id);
                    return (
                      <button
                        key={a.id}
                        type="button"
                        disabled={isCurrent}
                        onClick={() => toggle(a)}
                        className={`w-full flex items-center gap-3 text-left px-3 py-2.5 transition-colors ${isCurrent ? 'bg-orange-50 cursor-default' : 'hover:bg-gray-50'}`}
                        data-testid={`customer-group-result-${a.id}`}
                      >
                        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${isPicked ? 'border-orange-500 bg-orange-500' : 'border-gray-300'}`}>
                          {isPicked && <Check className="w-3.5 h-3.5 text-white" />}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-sm text-gray-900 truncate">{a.name}</div>
                          <div className="text-xs text-gray-500">
                            {[a.city, a.customer_code ? `#${a.customer_code}` : null].filter(Boolean).join(' · ')}
                            {isCurrent ? ' · this account' : ''}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : query.trim().length >= 2 ? (
                <div className="text-center py-8 text-sm text-gray-400">No accounts match "{query}"</div>
              ) : (
                <div className="text-center py-8 text-sm text-gray-400">Search to find the customer's other accounts</div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-orange-100 flex-shrink-0">
              <Button variant="outline" onClick={onClose} disabled={saving} className="rounded-xl h-10 px-4 text-sm">Cancel</Button>
              <Button
                onClick={create}
                disabled={saving || picked.size < 2}
                className="rounded-xl h-10 px-5 text-sm text-white font-semibold"
                style={{ backgroundColor: ORANGE }}
                data-testid="customer-group-create-btn"
              >
                {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Grouping…</> : `Group ${picked.size} Accounts`}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
